import type { ChangeEvent, FC } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Box,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import classNames from 'classnames';

import useBusinessUnits from 'src/components/app/context/BusinessUnitsContext';
import ErrorMessage from 'src/components/common/Messages/ErrorMessage';
import type { UserAdminDetailHistoryState } from 'src/components/userAdmin/UserAdminDetailContext';
import useUserDetail from 'src/components/userAdmin/UserAdminDetailContext';

import { styles } from './index.styles';

export const USER_SETTINGS_BUSINESS_UNIT_DROPDOWN =
  'user-settings-business-unit-dropdown';

const FIELD_NAMES: ['email', 'first_name', 'last_name'] = [
  'email',
  'first_name',
  'last_name',
];

interface UserSettingsProps {}

const UserSettings: FC<UserSettingsProps> = () => {
  const { t } = useTranslation('admin');
  const { businessUnits } = useBusinessUnits();
  const { fieldErrors, handleFieldChange, user } = useUserDetail();
  const history = useHistory<UserAdminDetailHistoryState>();
  const newUserEmail = history.location.state?.newUserEmail;

  useEffect(() => {
    if (newUserEmail && (!user.email || user.email === '')) {
      handleFieldChange({
        target: { name: 'email', value: newUserEmail },
      } as ChangeEvent<HTMLInputElement>);
    }
  }, [newUserEmail, user.email]);

  return (
    <Stack direction="row" sx={{ gap: 0.75, ...styles.inputs }}>
      {FIELD_NAMES.map((fieldName) => (
        <Box sx={styles.input} key={fieldName}>
          <Box
            component="label"
            sx={fieldErrors[fieldName] ? styles.fieldError : undefined}
            htmlFor={fieldName}
          >
            <Typography sx={{ fontWeight: 600, pb: '0.5rem' }}>
              {t(`admin:Detail.${fieldName}`)}
            </Typography>
          </Box>
          <TextField
            className={classNames({
              'user-admin-detail-input-error': fieldErrors[fieldName],
            })}
            id={fieldName}
            key={fieldName}
            name={fieldName}
            onBlur={handleFieldChange as (event: ChangeEvent) => void}
            onChange={handleFieldChange as (event: ChangeEvent) => void}
            placeholder={t(`admin:Detail.${fieldName}`)}
            value={user[fieldName]}
          />

          {fieldErrors[fieldName] && (
            <ErrorMessage align="left">{fieldErrors[fieldName]}</ErrorMessage>
          )}
        </Box>
      ))}
      <Box
        data-test-id={USER_SETTINGS_BUSINESS_UNIT_DROPDOWN}
        component="label"
        sx={fieldErrors.business_unit_id ? styles.fieldError : { flex: 1 }}
        id="admin-is-active-setting"
      >
        <Typography sx={{ fontWeight: 600, pb: '0.5rem' }}>
          {t('admin:Detail.BusinessUnit')}
        </Typography>
        <Select
          sx={styles.dropdown}
          className="user-admin-detail-select-wrapper-select"
          name="business_unit_id"
          aria-labelledby="admin-is-active-setting"
          onChange={handleFieldChange}
          value={user.business_unit_id || ''}
          variant="outlined"
          displayEmpty
          renderValue={(selected) =>
            !selected ? (
              <Box sx={styles.placeholder}>
                {t('admin:Detail.BusinessUnit')}
              </Box>
            ) : (
              businessUnits.find(({ id }) => id === selected)?.name
            )
          }
        >
          {businessUnits.map((businessUnit, index) => (
            <MenuItem key={index} value={businessUnit.id}>
              {businessUnit.name}
            </MenuItem>
          ))}
        </Select>

        {fieldErrors.business_unit_id && (
          <ErrorMessage align="left">
            {fieldErrors.business_unit_id}
          </ErrorMessage>
        )}
      </Box>
      {fieldErrors.role && (
        <ErrorMessage align="left">{fieldErrors.role}</ErrorMessage>
      )}
    </Stack>
  );
};

export default UserSettings;
